import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { Card, CardContent } from '@Components/Card';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import {
  EventArchivedBadge,
  EventStatusBadge,
  EventVisibilityBadge,
} from '@Components/TicketedEvents/Badges';
import VenueQuickFilter from '@Components/CustomDataTableFilters/VenueQuickFilter';
import { useEvents } from '@Hooks/useEvents';
import type { Event } from '@Types/events';
import {
  buildFilterParams,
  formatZonedDateTime,
  getStatusSortKey,
} from '@Utils/utils';
import { useQueryStateFixed } from '@Utils/nuqs-fix';
import { parseAsBoolean } from 'nuqs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const NOT_AVAILABLE = 'N/A';

const eventColumnConfigs: ColumnConfig<Event>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name ?? NOT_AVAILABLE,
    type: 'text',
    size: 260,
    cell: (info) => {
      const event = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <span>{event.name ?? NOT_AVAILABLE}</span>
          <EventArchivedBadge event={event as any} />
        </div>
      );
    },
  },
  {
    id: 'venueId',
    headerLabel: 'Venue',
    accessor: (row) => row.venue?.name ?? NOT_AVAILABLE,
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
  {
    id: 'startDateTime',
    headerLabel: 'Start Date',
    accessor: (row) => row.startDateTime ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return NOT_AVAILABLE;
      const dateString = formatZonedDateTime(
        date,
        "MMMM dd, yyyy 'at' hh:mm a",
      );
      return dateString || NOT_AVAILABLE;
    },
  },
  {
    id: 'endDateTime',
    headerLabel: 'End Date',
    accessor: (row) => row.endDateTime ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return NOT_AVAILABLE;
      const dateString = formatZonedDateTime(
        date,
        "MMMM dd, yyyy 'at' hh:mm a",
      );
      return dateString || NOT_AVAILABLE;
    },
  },
  {
    id: 'status',
    headerLabel: 'Status',
    accessor: (row) => row.status ?? NOT_AVAILABLE,
    type: 'text',
    customSortingFn: (rowA, rowB) => {
      const keyA = getStatusSortKey(rowA.original);
      const keyB = getStatusSortKey(rowB.original);
      return keyA - keyB;
    },
    cell: (info) => {
      const event = info.row.original;
      return <EventStatusBadge event={event as any} />;
    },
  },
  {
    id: 'visibility',
    headerLabel: 'Visibility',
    accessor: (row) => row.visibility ?? NOT_AVAILABLE,
    type: 'text',
    cell: (info) => {
      const event = info.row.original;
      return <EventVisibilityBadge event={event as any} />;
    },
  },
];

export default function EventsList() {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useQueryStateFixed<boolean>(
    'archived',
    parseAsBoolean.withDefault(false),
  );

  const controller = useResourceTableController<Event>({
    columnsConfig: eventColumnConfigs,
    defaultSort: {
      id: 'startDateTime',
      desc: true,
    },
    baseSearchParams: () => ({
      include: 'venue',
      ...(showArchived ? {} : { 'filter[archivedAt]': 'null' }),
    }),
    buildFilterParams: (columnFilters) => buildFilterParams(columnFilters),
    quickFilterFields: [{ field: 'name', label: 'Name' }],
    initialPageSize: 10,
    initialPinning: {
      left: ['name'],
      right: [],
    },
  });

  const { data: payload, status } = useEvents({
    searchParams: controller.searchParams,
  });

  const rows = useMemo(() => {
    const rawRows: Event[] = Array.isArray(payload)
      ? payload
      : (payload?.data ?? []);

    const sortConfig = controller.serverSorting?.find((s) => s.id === 'status');
    if (!sortConfig) return rawRows;

    return [...rawRows].sort((a, b) => {
      const keyA = getStatusSortKey(a);
      const keyB = getStatusSortKey(b);
      const diff = keyA - keyB;
      return sortConfig.desc ? -diff : diff;
    });
  }, [payload, controller.serverSorting]);

  const meta = Array.isArray(payload) ? undefined : payload?.meta;

  return (
    <Card>
      <CardContent>
        <ResourceTableView<Event>
          label="Events"
          controller={controller}
          data={rows}
          meta={meta}
          status={status}
          onRowClick={(row) => navigate(`/events/${row.id}`)}
          renderQuickFiltersExtra={({ table }) => (
            <>
              <VenueQuickFilter<Event> table={table} />
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-archived-events"
                  checked={showArchived}
                  onCheckedChange={(checked) =>
                    setShowArchived(checked === true)
                  }
                />
                <Label htmlFor="show-archived-events">Show archived</Label>
              </div>
            </>
          )}
        />
      </CardContent>
    </Card>
  );
}
